import React, { useState } from "react";
import { PrimaryButton, SecondaryButton } from "../../components";
import {
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import dayjs from "dayjs";

interface Props {
  onApplyFilters: (filters: {
    status?: string;
    day?: number;
    month?: number;
    year?: number;
  }) => void;
  onClearFilters: () => void;
}

type Period = "yearly" | "monthly" | "daily";

export function WithdrawFilters({ onApplyFilters, onClearFilters }: Props) {
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [period, setPeriod] = useState<Period | undefined>(undefined);
  const [year, setYear] = useState<number | undefined>(undefined);
  const [month, setMonth] = useState<number | undefined>(undefined);
  const [day, setDay] = useState<number | undefined>(undefined);
  const [dailyDateInput, setDailyDateInput] = useState<string>("");

  const handleApplyFilters = () => {
    let filterDay = day;
    let filterMonth = month;
    let filterYear = year;

    if (period === "daily" && dailyDateInput) {
      const date = dayjs(dailyDateInput);
      if (date.isValid()) {
        filterYear = date.year();
        filterMonth = date.month() + 1; // dayjs months are 0-indexed
        filterDay = date.date();
      } else {
        // Handle invalid date input, maybe show an error or clear the field
        console.error("Invalid date format for daily filter");
        filterDay = undefined;
        filterMonth = undefined;
        filterYear = undefined;
      }
    } else if (period === "monthly") {
      filterDay = undefined; // Day is not applicable for monthly
    } else if (period === "yearly") {
      filterDay = undefined; // Day is not applicable for yearly
      filterMonth = undefined; // Month is not applicable for yearly
    }

    onApplyFilters({
      status,
      day: filterDay,
      month: filterMonth,
      year: filterYear,
    });
  };

  const handleClearFilters = () => {
    setStatus(undefined);
    setPeriod(undefined);
    setYear(undefined);
    setMonth(undefined);
    setDay(undefined);
    setDailyDateInput("");
    onClearFilters();
  };

  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - i
  );
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  return (
    <div className="flex flex-col w-full p-2 sm:p-7 bg-white  border-gray-border drop-shadow-none rounded-xl mb-1 sm:mb-10">
      <div className="flex flex-col sm:flex-row w-full items-start justify-between">
        <div className="flex flex-row items-start justify-start">
          <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-12">
            Audit Log Filters
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row items-end justify-end mb-4 space-y-4 sm:space-y-0 sm:space-x-4">
        {/* Status Filter */}
        <Menu>
          <MenuHandler>
            <button className="h-10 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
              {status
                ? status.charAt(0).toUpperCase() + status.slice(1)
                : "All"}
              <ChevronDown className="ml-2" />
            </button>
          </MenuHandler>
          <MenuList placeholder={undefined}>
            <MenuItem
              onClick={() => setStatus("")}
              className="font-satoshi text-base text-dark"
              placeholder={undefined}
            >
              All
            </MenuItem>
            <MenuItem
              onClick={() => setStatus("REDEEMED")}
              className="font-satoshi text-base text-dark"
              placeholder={undefined}
            >
              Redeemed
            </MenuItem>
            <MenuItem
              onClick={() => setStatus("CREATED")}
              className="font-satoshi text-base text-dark"
              placeholder={undefined}
            >
              Created
            </MenuItem>
          </MenuList>
        </Menu>

        {/* Period Filter */}
        <Menu>
          <MenuHandler>
            <button className="h-10 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
              {period
                ? period.charAt(0).toUpperCase() + period.slice(1)
                : "Select Period"}
              <ChevronDown className="ml-2" />
            </button>
          </MenuHandler>
          <MenuList placeholder={undefined}>
            <MenuItem
              onClick={() => setPeriod("yearly")}
              className="font-satoshi text-base text-dark"
              placeholder={undefined}
            >
              Yearly
            </MenuItem>
            <MenuItem
              onClick={() => setPeriod("monthly")}
              className="font-satoshi text-base text-dark"
              placeholder={undefined}
            >
              Monthly
            </MenuItem>
            <MenuItem
              onClick={() => setPeriod("daily")}
              className="font-satoshi text-base text-dark"
              placeholder={undefined}
            >
              Daily
            </MenuItem>
          </MenuList>
        </Menu>

        {period === "yearly" || period === "monthly" ? (
          <Menu>
            <MenuHandler>
              <button className="h-10 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                {year || "Select Year"}
                <ChevronDown className="ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              <MenuItem
                onClick={() => setYear(undefined)}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Select Year
              </MenuItem>
              {years.map((y) => (
                <MenuItem
                  key={y}
                  onClick={() => setYear(y)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {y}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        ) : null}

        {period === "monthly" ? (
          <Menu>
            <MenuHandler>
              <button className="h-10 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                {month
                  ? dayjs()
                      .month(month - 1)
                      .format("MMMM")
                  : "Select Month"}
                <ChevronDown className="ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              <MenuItem
                onClick={() => setMonth(undefined)}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Select Month
              </MenuItem>
              {months.map((m) => (
                <MenuItem
                  key={m}
                  onClick={() => setMonth(m)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {dayjs()
                    .month(m - 1)
                    .format("MMMM")}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        ) : null}

        {period === "daily" ? (
          <input
            type="date"
            value={dailyDateInput}
            onChange={(e) => setDailyDateInput(e.target.value)}
            className="h-10 px-3 border rounded border-gray-border bg-white font-satoshi font-medium text-gray-text text-sm"
          />
        ) : null}
      </div>
      <div className="flex flex-row items-end justify-end space-x-4">
        {/* Action Buttons */}
        <PrimaryButton
          label="Apply Filters"
          onClick={handleApplyFilters}
          className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
          size="sm"
        />
        <SecondaryButton
          label="Clear"
          onClick={handleClearFilters}
          className="mr-3 h-8 px-4 py-0 bg-white border border-droppie-oranje text-droppie-oranje hover:bg-droppie-oranje/5 flex items-center justify-center"
          labelClassName="text-droppie-oranje"
        />
      </div>
    </div>
  );
}
